import React, { useEffect, useReducer } from "react";
import { OrdersSettingReducer, initalState } from "./state";
import { Tabs } from "./Ui/Tabs";
import { useCurrencyState } from "../../yoello-lib/modules/hooks/useCurrencyState";
import { ITabs, TabContainer } from "./types.d";
import { IYoelloTheme } from "../../yoello-lib/modules/themes/IYoelloTheme";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import {
  ASAPDeliveryOption,
  GeneralForm,
  NewOrdersAutoAccept,
  OrderTypesDelivery,
  OrderTypesDinInForm
} from "../../formSchemas/ordersSetting.form";
import TippingComponent from "../tipping/TippingComponent";
import { FormsContainer } from "./Forms/FormsContainer";
import { setActiveTabState } from "./state";
import { useTranslation } from "react-i18next";
import { CollectionContainer } from "./CollectionContainer";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { COLLECTION_URL, DELIVERY_URL, DINE_URL, dialogKeys } from "../../const/URL.const";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PopUpMessagesForm } from "./Forms/PopUpMessagesForm";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../reducers";
import { OnboardingSources } from "../signup/types";
import setLocationDialog from "./Ui/setLocationDialog";
import { MultiDialogRoute } from "../dialogRoutes/MultiDialogRoute";
import { useDialogRouteStages } from "../dialogRoutes/useDialogRouteStages";
import SelectLocationOnMap from "./Ui/selectLocationOnMap";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";

const ordersSettingTabs: ITabs = {
  direction: "column",
  tabs: [
    {
      key: "General",
      title: "forms.orders.ordersSettingTabs.general",
    },
    {
      key: "Dine-In",
      title: "forms.orders.ordersSettingTabs.dineIn",
    },
    {
      key: "Delivery",
      title: "forms.orders.ordersSettingTabs.delivery",
    },
    {
      key: "Collection",
      title: "forms.orders.ordersSettingTabs.collection",
    },
  ],
};
const tabURLS: Record<string, string> = {
  "Collection": COLLECTION_URL,
  "Delivery": DELIVERY_URL,
  "Dine-In": DINE_URL,
  "General": "/"
}
const useStyles = makeStyles((theme: IYoelloTheme) =>
  createStyles({
    root: {
      display: "flex",
      "@media (max-width: 1000px)": {
        flexDirection: "column!important",
      },
    },
    content: {
      flex: 1,
    },
  })
);
interface IOrdersSettingContainer {
  settings: any;
  onFormSubmit: (data: any) => Promise<any>;
}
export function OrdersSettingContainer({
  onFormSubmit,
  settings
}: IOrdersSettingContainer) {
  const [state, dispatch] = useReducer(OrdersSettingReducer, initalState);
  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );
  const venue = useSelector((state: IReduxAppState) => state.venue[selectedVenueId]?.venueData);
  const history = useHistory();
  let { path } = useRouteMatch();
  const { t } = useTranslation();
  const { activeTab } = state;
  const classes = useStyles();
  const currency = useCurrencyState();
  const { enableOrderOnly } = useFlags();

  const VenueLocation = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueLocation
  );
  
  const { goToNextStep } = useDialogRouteStages(history);

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.ORDER_SETTINGS_MANAGEMENT);
  const onboarding_source  = venue?.onboarding_source;

  const {callAPI, isLoading, apiData} = useAPIV2<any>(APIExtensions.venues,{
    query: `${selectedVenueId}/locations`,
    onMountDisabled: false
  })

  const props = {
    ...ordersSettingTabs,
    onChange: changeTab,
    activeTab,
  };
  useEffect(() => {
    const pathName = history.location.pathname;
    /**setup tab based on url */
    for (const key in tabURLS) {
      const url = tabURLS[key];
      if (pathName.includes(url)) {
        changeTab({ key });
        break;
      }
    }
    //eslint-disable-next-line
  }, []);

  function changeTab(tab: TabContainer) {
    const activeTab = {
      key: tab.key,
    };
    dispatch(setActiveTabState(activeTab));
    history.push(`${path}${tabURLS[tab.key]}`);
  }

  function onClickSetLocation() {
		goToNextStep(
			dialogKeys.SETLOCATION,
			{
				ComponentProps: {},
				dialogTitle: t("selectLocation.addYourAddress"),
			}, `${path}${DELIVERY_URL}/address-form`, null)
	}

  return (
    <div className={classes.root}>
      <Tabs {...props} />
      <div className={classes.content}>
        <Switch>
          <Route exact path={path}>
            <>
              <FormsContainer 
                profileForms={GeneralForm(t , enableOrderOnly)} 
                onFormSubmit={onFormSubmit} 
                settings={settings} 
                formKey={"General"} 
                isDisabled={!hasPermission}
                onFailedInteraction={onFailedInteraction}
              />
              <TippingComponent />
              <PopUpMessagesForm/>
              {onboarding_source === OnboardingSources.partners && <FormsContainer profileForms={NewOrdersAutoAccept()}
                  onFormSubmit={onFormSubmit}
                  settings={settings}
                  formKey={"AUTO-ACCEPT-ORDERS"} 
                  isDisabled={!hasPermission}
                  onFailedInteraction={onFailedInteraction}
              />}
              <FormsContainer profileForms={ASAPDeliveryOption()}
                  onFormSubmit={onFormSubmit}
                  settings={settings}
                  formKey={"ASAP-DELIVERY-OPTION"} 
                  onFailedInteraction={onFailedInteraction}
              />
            </>
          </Route>
          <Route path={`${path}${COLLECTION_URL}`}>
            <CollectionContainer onFormSubmit={onFormSubmit} />
          </Route>
          <Route path={`${path}${DINE_URL}`}>
            <FormsContainer profileForms={OrderTypesDinInForm(t)}
              onFormSubmit={onFormSubmit}
              settings={settings}
              formKey={"Dine-In"} 
              isDisabled={!hasPermission}
              onFailedInteraction={onFailedInteraction}
            />
          </Route>
          <Route path={`${path}${DELIVERY_URL}`}>
            <FormsContainer 
              profileForms={OrderTypesDelivery(
                currency?.iso_code === "GBP", 
                onClickSetLocation, 
                Boolean(apiData?.[0]?.latitude), 
                VenueLocation?.address_country === "GB"
              )}
              onFormSubmit={onFormSubmit}
              settings={settings}
              formKey={"Delivery"} 
              isDisabled={!hasPermission}
              onFailedInteraction={onFailedInteraction}
            />
            <MultiDialogRoute
              navigateBackRotue={`${path}${DELIVERY_URL}`}
              RouteSteps={[
                {
                  key: dialogKeys.SETLOCATION,
                  Component: setLocationDialog,
                  path: `${path}${DELIVERY_URL}/address-form`,
                  exact: true,
                  title: "Location",
                  isFormDialog:true,
                  componentProps: {
                    hasFrame: false,
                    path,
                    locationCallApi: callAPI,
                    isLoading,
                  },
                },
                {
                  key: dialogKeys.SELECTONMAP,
                  Component: SelectLocationOnMap,
                  path: `${path}${DELIVERY_URL}/map`,
                  exact: true,
                  title: "Location",
                  isFormDialog:false,
                  componentProps: {
                    hasFrame: false,
                    path,
                    history,
                  },
                }
              ]}
            />                
          </Route>
        </Switch>
      </div>
    </div>
  );
}
